// Costs for billable work (PRD rev. 1.23, FR10): the project-and-treatment picker used on the Accounts
// tab, in Reports' lines and on the Code costs tab, and the Code costs tab itself: QuickBooks spending
// often for a project, with suggestions, coded several at once, and billed-back costs to rebill.
// Admins only (books.read, FR10.6).

import { useState } from "react";
import { Badge, Body1, Button, Caption1, Card, Checkbox, Select, Subtitle2 } from "@fluentui/react-components";
import { CODING_SOURCE_LABEL, TREATMENT_LABEL, type CodingProject, type CostTreatment, type ResolvedCoding, type SpendCategory } from "../../../core";
import { errorText, send, useApi } from "../api";
import { cents, day } from "../format";
import { ErrorBar, Loading } from "../ui";

export interface Pick {
  code: string;
  treatment: CostTreatment;
}

const projectLabel = (p: CodingProject) => `${p.code} · ${p.name}${p.internal ? "" : ` (${p.clientName})`}`;

/** A project (or none) and who bears it. A client project can't be a company cost, nor an internal code billed back. */
export function CodingPicker({ projects, value, onChange }: { projects: readonly CodingProject[]; value: Pick; onChange: (v: Pick) => void }) {
  const p = projects.find((x) => x.code === value.code);
  const allowed: CostTreatment[] = !value.code ? ["company"] : p?.internal ? ["project"] : ["project", "billed"];
  const fix = (code: string, t: CostTreatment): Pick => {
    const q = projects.find((x) => x.code === code);
    const ok: CostTreatment[] = !code ? ["company"] : q?.internal ? ["project"] : ["project", "billed"];
    return { code, treatment: ok.includes(t) ? t : ok[0] };
  };
  return (
    <span className="inline-field">
      <Select size="small" aria-label="Project" value={value.code} onChange={(_, d) => onChange(fix(d.value, value.treatment))}>
        <option value="">No project</option>
        {projects
          .filter((x) => x.active || x.code === value.code)
          .map((x) => (
            <option key={x.code} value={x.code}>
              {projectLabel(x)}
            </option>
          ))}
      </Select>
      <Select size="small" aria-label="Who bears it" value={value.treatment} onChange={(_, d) => onChange(fix(value.code, d.value as CostTreatment))}>
        {allowed.map((t) => (
          <option key={t} value={t}>
            {p?.internal && t === "project" ? "Shared cost (internal code)" : TREATMENT_LABEL[t]}
          </option>
        ))}
      </Select>
    </span>
  );
}

/** How a line's coding reads in a table. */
export function codingText(c: Pick | ResolvedCoding | null | undefined, projects: readonly CodingProject[]): string {
  if (!c || (!c.code && c.treatment === "company")) return "Company cost";
  const p = projects.find((x) => x.code === c.code);
  return `${c.code}${p ? ` · ${p.name}` : ""}: ${p?.internal ? "Shared cost" : TREATMENT_LABEL[c.treatment]}`;
}

/** A line's coding, with an editor that opens in place (Reports, FR10.2). */
export function LineCoding({ codingKey, coding, projects, onSaved }: { codingKey: string; coding: ResolvedCoding; projects: readonly CodingProject[]; onSaved: () => void }) {
  const [editing, setEditing] = useState<Pick | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const save = async (body: Record<string, unknown>) => {
    setBusy(true);
    setError(undefined);
    try {
      await send("PUT", "/books/coding", { keys: [codingKey], ...body });
      setEditing(null);
      onSaved();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  if (coding.source === "expense-report") return <Caption1>Repays expense report {coding.report?.ref}: its own lines count</Caption1>;
  if (!editing)
    return (
      <span className="inline-field">
        <span>
          {codingText(coding, projects)}
          {coding.source !== "default" && <Caption1 className="block muted">{CODING_SOURCE_LABEL[coding.source]}</Caption1>}
        </span>
        <Button size="small" appearance="subtle" onClick={() => setEditing({ code: coding.code ?? "", treatment: coding.treatment })}>
          Code
        </Button>
      </span>
    );
  return (
    <span className="stack">
      <CodingPicker projects={projects} value={editing} onChange={setEditing} />
      <span className="inline-field">
        <Button size="small" appearance="primary" disabled={busy} onClick={() => void save({ code: editing.code, treatment: editing.treatment })}>
          Save
        </Button>
        {coding.source === "line" && (
          <Button size="small" disabled={busy} onClick={() => void save({ clear: true })}>
            Use the account's
          </Button>
        )}
        <Button size="small" appearance="subtle" disabled={busy} onClick={() => setEditing(null)}>
          Cancel
        </Button>
      </span>
      <ErrorBar message={error} />
    </span>
  );
}

/** An account's default coding on the Accounts tab, saved as soon as it's picked (FR10.2). */
export function AccountCoding({ accountId, coding, projects, onSaved }: { accountId: string; coding: { code?: string; treatment: CostTreatment } | null; projects: readonly CodingProject[]; onSaved: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const save = async (v: Pick) => {
    setBusy(true);
    setError(undefined);
    try {
      await send("PUT", "/books/coding/account", v.code || v.treatment !== "company" ? { accountId, ...v } : { accountId, clear: true });
      onSaved();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <span className={busy ? "muted" : undefined}>
      <CodingPicker projects={projects} value={{ code: coding?.code ?? "", treatment: coding?.treatment ?? "company" }} onChange={(v) => void save(v)} />
      <ErrorBar message={error} />
    </span>
  );
}

// --- The Code costs tab ---------------------------------------------------------------------------------

interface CodingRow {
  key: string;
  date: string;
  txnType: string;
  docNumber: string | null;
  partyName: string | null;
  memo: string | null;
  accountName: string;
  categoryId: string;
  amount: number;
  coding: ResolvedCoding;
}
type RebillState = { rebilled: false } | { rebilled: true; how: "matched" | "marked"; invoice: string };
export type RebillRow = CodingRow & { clientName: string; projectName: string; state: RebillState; age: number; flag: "ok" | "flagged" | "overdue" };
interface CodingResponse {
  projects: CodingProject[];
  categories: SpendCategory[];
  toCode: (CodingRow & { suggestion: { code: string; why: string } | null })[];
  coded: CodingRow[];
  rebill: RebillRow[];
  flagDays: number;
  dueDays: number;
}

const catName = (id: string, cats: readonly SpendCategory[]) => {
  const c = cats.find((x) => x.id === id);
  const parent = c?.parentId ? cats.find((x) => x.id === c.parentId) : undefined;
  return c ? (parent ? `${parent.name} › ${c.name}` : c.name) : id;
};

export function FlagBadge({ flag, age }: { flag: "ok" | "flagged" | "overdue"; age: number }) {
  if (flag === "ok") return <Caption1 className="muted">{age} days</Caption1>;
  return (
    <Badge size="small" appearance="tint" color={flag === "overdue" ? "danger" : "warning"}>
      {flag === "overdue" ? `Overdue, ${age} days` : `Bill soon, ${age} days`}
    </Badge>
  );
}

/** Marks a billed-back line rebilled by hand, or opens it again (FR10.4). */
export async function markRebilled(row: RebillRow) {
  if (row.state.rebilled && row.state.how === "marked") return send("PUT", "/books/coding/rebilled", { key: row.key, clear: true });
  const invoice = window.prompt("The QuickBooks invoice number it was rebilled on");
  if (!invoice?.trim()) return;
  return send("PUT", "/books/coding/rebilled", { key: row.key, invoice: invoice.trim() });
}

export function CodeCosts({ onChange }: { onChange: () => void }) {
  const data = useApi<CodingResponse>("/books/coding");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [pick, setPick] = useState<Pick>({ code: "", treatment: "project" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [showAll, setShowAll] = useState(false);
  const d = data.data;
  if (!d) return data.error ? <ErrorBar message={data.error} /> : <Loading />;
  const act = async (f: () => Promise<unknown>) => {
    setBusy(true);
    setError(undefined);
    try {
      await f();
      setSelected(new Set());
      data.reload();
      onChange();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  const rows = showAll ? d.toCode : d.toCode.slice(0, 100);
  const suggested = d.toCode.filter((r) => r.suggestion && selected.has(r.key));
  const toggle = (k: string, on: boolean) => {
    const next = new Set(selected);
    if (on) next.add(k);
    else next.delete(k);
    setSelected(next);
  };
  const applySuggestions = () =>
    act(async () => {
      const byCode = new Map<string, string[]>();
      for (const r of suggested) byCode.set(r.suggestion!.code, [...(byCode.get(r.suggestion!.code) ?? []), r.key]);
      for (const [code, keys] of byCode) await send("PUT", "/books/coding", { keys, code, treatment: "project" });
    });
  const open = d.rebill.filter((r) => !r.state.rebilled);
  return (
    <>
      <ErrorBar message={error} />
      <Card className="panel wide">
        <div className="panel-head">
          <Subtitle2>Costs to code</Subtitle2>
        </div>
        <Caption1>
          Travel, events and contractors that may be for a project, newest first. A cost for a client project goes to the project whether it's billable or not, so the project shows its whole program cost: <strong>Project cost</strong> counts against
          its margin; <strong>Billed back</strong> passes through at cost until it's rebilled. An internal code (3S001–3S003) is a shared cost. A contractor who logs time in Base Camp is already in the project's labor cost: leave them as a company
          cost. For a whole account, set its default on the Accounts tab.
        </Caption1>
        {d.toCode.length === 0 ? (
          <Body1 className="muted">Nothing waiting to be coded.</Body1>
        ) : (
          <>
            <div className="toolbar">
              <CodingPicker projects={d.projects} value={pick} onChange={setPick} />
              <Button size="small" appearance="primary" disabled={!selected.size || busy} onClick={() => void act(() => send("PUT", "/books/coding", { keys: [...selected], ...pick }))}>
                Code {selected.size || ""} selected
              </Button>
              <Button size="small" disabled={!suggested.length || busy} onClick={() => void applySuggestions()}>
                Use the suggestion for {suggested.length || ""} selected
              </Button>
            </div>
            <div className="table-scroll">
              <table className="edit-table">
                <thead>
                  <tr>
                    <th>
                      <Checkbox aria-label="Select all shown" checked={rows.length > 0 && rows.every((r) => selected.has(r.key))} onChange={(_, v) => setSelected(v.checked ? new Set(rows.map((r) => r.key)) : new Set())} />
                    </th>
                    <th>Date</th>
                    <th>Payee</th>
                    <th>Memo</th>
                    <th>Category</th>
                    <th className="num">Amount</th>
                    <th>Suggested</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.key}>
                      <td>
                        <Checkbox aria-label={`Select ${r.date} ${r.partyName ?? r.accountName}`} checked={selected.has(r.key)} onChange={(_, v) => toggle(r.key, !!v.checked)} />
                      </td>
                      <td>{day(r.date)}</td>
                      <td>
                        {r.partyName ?? ""}
                        <Caption1 className="block muted">
                          {r.txnType} {r.docNumber ?? ""} · {r.accountName}
                        </Caption1>
                      </td>
                      <td>{r.memo ?? ""}</td>
                      <td>{catName(r.categoryId, d.categories)}</td>
                      <td className="num">{cents(r.amount)}</td>
                      <td>
                        {r.suggestion ? (
                          <>
                            {codingText({ code: r.suggestion.code, treatment: "project" }, d.projects).replace(/: Project cost$/, "")}
                            <Caption1 className="block muted">{r.suggestion.why}</Caption1>
                          </>
                        ) : (
                          <Caption1 className="muted">—</Caption1>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {d.toCode.length > rows.length && (
              <Button size="small" appearance="subtle" onClick={() => setShowAll(true)}>
                Show all {d.toCode.length}
              </Button>
            )}
          </>
        )}
      </Card>

      <Card className="panel wide">
        <Subtitle2>Billed back, to rebill</Subtitle2>
        <Caption1>
          Billed-back costs stay here until they're rebilled: a QuickBooks invoice line for the client at the same amount takes them off by itself, or mark one rebilled with its invoice number. Flagged at {d.flagDays} days, overdue past {d.dueDays}.
        </Caption1>
        {d.rebill.length === 0 ? (
          <Body1 className="muted">No billed-back costs.</Body1>
        ) : (
          <RebillTable rows={d.rebill} busy={busy} onMark={(r) => void act(() => markRebilled(r) ?? Promise.resolve())} />
        )}
        {open.length > 0 && <Caption1 className="muted">{open.length} still to rebill, {cents(open.reduce((t, r) => t + r.amount, 0))}.</Caption1>}
      </Card>

      {d.coded.length > 0 && (
        <Card className="panel wide">
          <Subtitle2>Coded in Base Camp</Subtitle2>
          <div className="table-scroll">
            <table className="edit-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Payee</th>
                  <th>Memo</th>
                  <th className="num">Amount</th>
                  <th>Project and who bears it</th>
                </tr>
              </thead>
              <tbody>
                {d.coded.map((r) => (
                  <tr key={r.key}>
                    <td>{day(r.date)}</td>
                    <td>
                      {r.partyName ?? ""}
                      <Caption1 className="block muted">{r.accountName}</Caption1>
                    </td>
                    <td>{r.memo ?? ""}</td>
                    <td className="num">{cents(r.amount)}</td>
                    <td>
                      <LineCoding codingKey={r.key} coding={r.coding} projects={d.projects} onSaved={() => (data.reload(), onChange())} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </>
  );
}

export function RebillTable({ rows, busy, onMark }: { rows: RebillRow[]; busy: boolean; onMark: (r: RebillRow) => void }) {
  return (
    <div className="table-scroll">
      <table className="edit-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Client and project</th>
            <th>Payee</th>
            <th className="num">Amount</th>
            <th>Rebilled</th>
            <th aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key}>
              <td>{day(r.date)}</td>
              <td>
                {r.clientName}
                <Caption1 className="block muted">
                  {r.coding.code} · {r.projectName}
                </Caption1>
              </td>
              <td>
                {r.partyName ?? ""}
                {r.memo && <Caption1 className="block muted">{r.memo}</Caption1>}
              </td>
              <td className="num">{cents(r.amount)}</td>
              <td>{r.state.rebilled ? `On ${r.state.invoice}${r.state.how === "matched" ? " (matched in QuickBooks)" : ""}` : <FlagBadge flag={r.flag} age={r.age} />}</td>
              <td>
                {(!r.state.rebilled || r.state.how === "marked") && (
                  <Button size="small" appearance="subtle" disabled={busy} onClick={() => onMark(r)}>
                    {r.state.rebilled ? "Open again" : "Mark rebilled"}
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
